/**
 * Post-processing utility for generated blog HTML.
 *
 * Enforces structural constraints that may not always be honoured by the LLM:
 *   - Body <ul> and <ol> lists are trimmed to a maximum of 3 <li> items.
 *     Structural sections (TOC, internal-links, FAQ, conclusion, references)
 *     are exempt because their list counts are controlled by separate rules.
 */

const EXEMPT_SECTION_IDS = new Set([
  'table-of-contents',
  'inhoudsopgave',
  'internal-links',
  'interne-links',
  'faq',
  'conclusion',
  'conclusie',
  'references',
  'bronnen',
]);

const MAX_LIST_ITEMS = 3;

/**
 * Trims body list items to MAX_LIST_ITEMS and returns the serialised HTML string.
 * Safe to call in both browser (native DOMParser) and test (jsdom) environments.
 *
 * @param {string} html
 * @returns {string}
 */
export function normalizeBlogHtml(html) {
  if (!html) return html;

  let doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch (_) {
    return html;
  }

  const lists = doc.querySelectorAll('ul, ol');
  lists.forEach((list) => {
    // Skip lists inside exempt structural sections.
    const parentSection = list.closest('section[id]');
    if (parentSection && EXEMPT_SECTION_IDS.has(parentSection.id)) return;

    // Also skip the TOC <nav> element.
    if (list.closest('nav')) return;

    const items = Array.from(list.querySelectorAll(':scope > li'));
    if (items.length > MAX_LIST_ITEMS) {
      items.slice(MAX_LIST_ITEMS).forEach((li) => li.remove());
    }
  });

  // Serialise only the body children so we don't wrap output in <html><body>.
  return doc.body.innerHTML;
}
